import React from 'react';
import { Image, Platform, Text, View, StyleSheet } from 'react-native';

import { colors } from '../../core/theme/tokens';
import chatIcon from './assistantAvatarSource';

type IconName = 'close' | 'history' | 'new' | 'mic' | 'send' | 'back';

type Props = {
  name: IconName;
  size?: number;
  color?: string;
};

/**
 * Tiny chrome icons for the assistant. Web uses inline SVG; native uses glyphs.
 */
export function AssistantIcon({ name, size = 18, color = colors.text }: Props) {
  if (Platform.OS === 'web') {
    return <WebIcon name={name} size={size} color={color} />;
  }
  const glyph: Record<IconName, string> = {
    close: '✕',
    history: '☰',
    new: '+',
    mic: '◉',
    send: '↑',
    back: '←',
  };
  return (
    <Text style={{ color, fontSize: size, fontWeight: '700', lineHeight: size + 2 }}>
      {glyph[name]}
    </Text>
  );
}

function WebIcon({ name, size, color }: { name: IconName; size: number; color: string }) {
  const paths: Record<IconName, string> = {
    close: 'M6 6l12 12M18 6L6 18',
    history: 'M4 6h16M4 12h16M4 18h10',
    new: 'M12 5v14M5 12h14',
    mic: 'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zm0 14v3m-5-6a5 5 0 0 0 10 0',
    send: 'M5 12h12M13 6l6 6-6 6',
    back: 'M15 6l-6 6 6 6',
  };
  // react-native-web accepts DOM tags when created as intrinsic elements via createElement.
  return (
    <View style={{ width: size, height: size }} accessibilityElementsHidden>
      {React.createElement(
        'svg',
        {
          width: size,
          height: size,
          viewBox: '0 0 24 24',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg',
          style: { display: 'block' },
        },
        React.createElement('path', {
          d: paths[name],
          stroke: color,
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }),
      )}
    </View>
  );
}

export function AssistantAvatar({ size = 28 }: { size?: number }) {
  const radius = size / 2;
  return (
    <View
      style={[
        styles.avatar,
        { width: size, height: size, borderRadius: radius },
      ]}
      accessibilityRole="image"
      accessibilityLabel="Assistant"
    >
      <Image
        source={chatIcon}
        resizeMode="cover"
        accessibilityIgnoresInvertColors
        style={{ width: size, height: size, borderRadius: radius }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  avatar: {
    overflow: 'hidden',
    backgroundColor: '#05080C',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.35)',
  },
});
